import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiSquare, FiClock, FiCalendar, FiList, FiUsers, FiFilter } = FiIcons;

const TimeTracking = () => {
  const { tasks, activeTimer, startTimer, stopTimer, teamMembers } = useData();
  const { user, hasPermission } = useAuth();
  
  const [selectedTask, setSelectedTask] = useState('');
  const [description, setDescription] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [activeTab, setActiveTab] = useState('my-timesheet');
  const [filterEmployee, setFilterEmployee] = useState('all');

  // Filter tasks assigned to current user
  const myTasks = tasks.filter(t => t.assignedTo === user.id && t.status !== 'completed');

  // Timer logic
  useEffect(() => {
    let interval;
    if (activeTimer) {
      const startTime = activeTimer.startTime;
      interval = setInterval(() => {
        setElapsed(Date.now() - startTime);
      }, 1000);
    } else {
      setElapsed(0);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  const formatTime = (ms) => {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / 1000 / 60) % 60);
    const hours = Math.floor(ms / 1000 / 60 / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleStartTimer = () => {
    if (selectedTask) {
      startTimer(parseInt(selectedTask), description);
      setDescription('');
    }
  };

  // Get logs
  const getLogs = (isTeamView) => {
    const logs = [];
    tasks.forEach(task => {
      if (task.timeLog) {
        task.timeLog.forEach(log => {
          // Filter: if Team View, check employee filter. If My View, check user id.
          if (isTeamView) {
            if (filterEmployee === 'all' || log.userId === parseInt(filterEmployee)) {
              logs.push({ ...log, taskTitle: task.title, taskId: task.id });
            }
          } else {
            if (log.userId === user.id) {
              logs.push({ ...log, taskTitle: task.title, taskId: task.id });
            }
          }
        });
      }
    });
    return logs.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const logs = getLogs(activeTab === 'team-timesheet');
  const showTeamTab = hasPermission('view_team') || hasPermission('all');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Time Tracking</h1>
        <p className="text-gray-600 mt-1">Track your work hours and manage timesheets</p>
      </div>

      {/* Timer Widget - Always for current user */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center space-x-2 mb-6">
          <SafeIcon icon={FiClock} className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Timer</h2>
        </div>

        {activeTimer ? (
          <div className="flex flex-col md:flex-row items-center justify-between bg-blue-50 p-6 rounded-xl border border-blue-100">
            <div className="mb-4 md:mb-0">
              <p className="text-sm text-blue-600 font-medium mb-1">Currently Working On</p>
              <h3 className="text-xl font-bold text-gray-900">
                {tasks.find(t => t.id === activeTimer.taskId)?.title}
              </h3>
              <p className="text-gray-600 text-sm">{activeTimer.description || 'No description'}</p>
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-4xl font-mono font-bold text-blue-600">
                {formatTime(elapsed)}
              </div>
              <button
                onClick={stopTimer}
                className="flex items-center space-x-2 bg-red-500 text-white px-6 py-3 rounded-lg hover:bg-red-600 transition-colors shadow-sm"
              >
                <SafeIcon icon={FiSquare} className="w-4 h-4 fill-current" />
                <span className="font-medium">Stop Timer</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <select
                value={selectedTask}
                onChange={(e) => setSelectedTask(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a task...</option>
                {myTasks.map(task => (
                  <option key={task.id} value={task.id}>{task.title}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-1">
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What are you working on?"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-1">
              <button
                onClick={handleStartTimer}
                disabled={!selectedTask}
                className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SafeIcon icon={FiPlay} className="w-4 h-4" />
                <span className="font-medium">Start Timer</span>
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Timesheet Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200"
      >
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('my-timesheet')}
              className={`flex items-center space-x-2 pb-2 border-b-2 transition-colors ${
                activeTab === 'my-timesheet' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <SafeIcon icon={FiList} className="w-4 h-4" />
              <span className="font-medium">My Logs</span>
            </button>
            
            {showTeamTab && (
              <button
                onClick={() => setActiveTab('team-timesheet')}
                className={`flex items-center space-x-2 pb-2 border-b-2 transition-colors ${
                  activeTab === 'team-timesheet' 
                    ? 'border-blue-600 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <SafeIcon icon={FiUsers} className="w-4 h-4" />
                <span className="font-medium">Team Logs</span>
              </button>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {activeTab === 'team-timesheet' && (
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-400" />
                <select
                  value={filterEmployee}
                  onChange={(e) => setFilterEmployee(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Employees</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="text-sm font-medium text-gray-700 bg-gray-50 px-3 py-1 rounded-lg border border-gray-100">
              Total: {logs.reduce((acc, log) => acc + log.hours, 0).toFixed(2)}h
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                {activeTab === 'team-timesheet' && (
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Employee</th>
                )}
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Task</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === 'team-timesheet' ? 5 : 4} className="px-6 py-8 text-center text-gray-500">
                    No time logs found
                  </td>
                </tr>
              ) : (
                logs.map((log, index) => {
                  const employee = teamMembers.find(m => m.id === log.userId);
                  return (
                    <tr key={`${log.taskId}-${index}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                          <span>{format(new Date(log.date), 'MMM dd, yyyy')}</span>
                        </div>
                      </td>
                      {activeTab === 'team-timesheet' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center space-x-2">
                             <img src={employee?.avatar} alt="" className="w-6 h-6 rounded-full" />
                             <span>{employee?.name || 'Unknown'}</span>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {log.taskTitle}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {log.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-blue-600">
                        {log.hours}h
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default TimeTracking;